import { useEffect, useState, useRef, useCallback } from 'react';

export interface TypeaheadDataType {
    displayText: string;
    [key: string]: any; // Allow additional properties
}

interface TypeaheadDropdownProps {
    query: string;
    getData: (query: string, page?: number, limit?: number, signal?: AbortSignal) => Promise<any>;
    itemClick: (item: TypeaheadDataType) => void;
    wrapperClass?: string;
    /** Replaces the default container look; positioning still comes from wrapperClass. */
    containerClassName?: string;
    itemClassName?: string;
    renderItem?: (item: TypeaheadDataType) => React.ReactNode;
    debounceTime?: number;
}

const DEFAULT_CONTAINER_CLASS = 'bg-white border-black border-2 p-1 rounded-md h-[200px] overflow-y-auto';
const DEFAULT_ITEM_CLASS = 'bg-gray my-1 p-1 rounded-md';

const TypeaheadDropdown = ({
    query,
    getData,
    itemClick,
    wrapperClass,
    containerClassName = DEFAULT_CONTAINER_CLASS,
    itemClassName = DEFAULT_ITEM_CLASS,
    renderItem,
    debounceTime = 300
}: TypeaheadDropdownProps) => {
    const [data, setData] = useState<Array<TypeaheadDataType>>([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const observer = useRef<IntersectionObserver | null>(null);
    const loadingRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const requestRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);

    const dedupeItems = (items: Array<TypeaheadDataType>) => {
        const seen = new Set<string>();
        return items.filter((item) => {
            const key = `${item.placeId || ''}|${(item.displayText || '').trim().toLowerCase()}`;
            if (!item.displayText || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const loadData = async (reset: boolean, requestId: number, signal?: AbortSignal) => {
        if (!query) return;
        if (!reset && isLoading) return;

        try {
            setIsLoading(true);
            const currentPage = reset ? 0 : page;
            const response = await getData(query, currentPage, undefined, signal);
            if (requestId !== requestRef.current) return;

            const incoming = dedupeItems(Array.isArray(response) ? response : response.genres || []);
            if (reset) {
                setData(incoming);
                setActiveIndex(0);
            } else {
                setData(prevData => dedupeItems([...prevData, ...incoming]));
            }

            setHasMore(response.hasMore || false);
            setPage(currentPage + 1);
        } catch (error) {
            if (signal?.aborted || requestId !== requestRef.current) return;
            console.error('Error loading dropdown data:', error);
        } finally {
            if (requestId === requestRef.current) setIsLoading(false);
        }
    };

    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!query) {
            requestRef.current += 1;
            abortRef.current?.abort();
            setData([]);
            setIsLoading(false);
            return undefined;
        }

        debounceTimerRef.current = setTimeout(() => {
            const requestId = requestRef.current + 1;
            requestRef.current = requestId;
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setPage(0);
            setHasMore(true);
            void loadData(true, requestId, controller.signal);
        }, debounceTime);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [query, debounceTime]);

    // Set up intersection observer for infinite scrolling
    const lastItemRef = useCallback((node: HTMLDivElement) => {
        if (isLoading) return;

        if (observer.current) {
            observer.current.disconnect();
        }

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                void loadData(false, requestRef.current, abortRef.current?.signal);
            }
        });

        if (node) {
            observer.current.observe(node);
        }
    }, [isLoading, hasMore]);

    // Add click outside listener to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setData([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!data.length) return undefined;
        const onKey = (event: KeyboardEvent) => {
            const parent = dropdownRef.current?.parentElement;
            if (!parent || !parent.contains(document.activeElement)) return;
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((index) => (index + 1) % data.length);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((index) => (index - 1 + data.length) % data.length);
            } else if (event.key === 'Enter') {
                const item = data[activeIndex];
                if (!item) return;
                event.preventDefault();
                itemClick(item);
            } else if (event.key === 'Escape') {
                setData([]);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [activeIndex, data, itemClick]);

    return (
        data && data.length > 0 ?
            <div
                ref={dropdownRef}
                role="listbox"
                className={`typeahead-dropdown absolute z-50 left-0 right-0 ${containerClassName} ${wrapperClass}`}
            >
                {data.map((item, index) => (
                    <div
                        key={`${item.placeId || item.displayText}-${index}`}
                        role="option"
                        aria-selected={index === activeIndex}
                        ref={index === data.length - 1 ? lastItemRef : undefined}
                        className={`typeahead-item cursor-pointer ${itemClassName} ${index === activeIndex ? 'bg-purple-light/10 text-purple-light' : ''}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => itemClick(item)}
                    >
                        {renderItem ? renderItem(item) : item.displayText}
                    </div>
                ))}
                {isLoading && (
                    <div ref={loadingRef} className="text-center py-1 text-gray-500 text-sm">
                    Loading more...
                    </div>
                )}
            </div>
            :
            <></>
    );
};

export default TypeaheadDropdown;
